import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, Tab, Tabs, Typography } from '@mui/material';
import { PhoneIphone as PhoneIphoneIcon, ShoppingBag as ShoppingBagIcon } from '@mui/icons-material';
import PurchasesTab from './MyPurchasesTabs/PurchasesTab';
import PlanLibraryTab from './MyPurchasesTabs/PlanLibraryTab';

/**
 * Parent → My Purchases, in two tabs:
 *  - Purchases: what the family bought, with watermarked paper / answer downloads;
 *  - Mobile plan: everything a child's mobile plan includes, to print (`?tab=plan`).
 */
const MyPurchases = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'plan' ? 'plan' : 'purchases';
  const [purchaseCount, setPurchaseCount] = useState(null);
  const [covered, setCovered] = useState(null);

  const onCovered = useCallback((v) => setCovered(v), []);

  const switchTab = (_, value) => {
    const next = new URLSearchParams(params);
    if (value === 'plan') next.set('tab', 'plan');
    else next.delete('tab');
    setParams(next, { replace: true });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          My Purchases
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Print the question papers and answers for everything you bought, and for everything included in your
          child&apos;s mobile plan.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={switchTab}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mb: 2.5,
          borderBottom: '1px solid #e5e7eb',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 52 },
          '& .Mui-selected': { color: '#3B2A9F !important' },
          '& .MuiTabs-indicator': { bgcolor: '#3B2A9F', height: 3, borderRadius: 3 },
        }}
      >
        <Tab
          value="purchases"
          icon={<ShoppingBagIcon fontSize="small" />}
          iconPosition="start"
          label={purchaseCount != null ? `Purchases (${purchaseCount})` : 'Purchases'}
        />
        <Tab
          value="plan"
          icon={<PhoneIphoneIcon fontSize="small" />}
          iconPosition="start"
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              Mobile plan
              {covered && <Chip label="Active" size="small" color="success" sx={{ height: 20, fontWeight: 700 }} />}
            </Box>
          }
        />
      </Tabs>

      {/* Both stay mounted so switching tabs keeps filters, scroll position and loaded pages. */}
      <Box hidden={tab !== 'purchases'}>
        <PurchasesTab onCount={setPurchaseCount} />
      </Box>
      <Box hidden={tab !== 'plan'}>
        <PlanLibraryTab onCovered={onCovered} />
      </Box>
    </Box>
  );
};

export default MyPurchases;
